import { defineMutator, type ReadonlyJSONObject } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { ADMIN_PLUS } from "../constants";
import type { MutateSurface } from "../message-builders";
import type { MutatorCtx } from "./messages-cores";

/**
 * Q&A / forum affordances for PUBLIC-workspace channels: a thread TITLE and a
 * thread-scoped ACCEPTED ANSWER. Both live on the thread-ROOT message's
 * `payload` JSONB — `kind:"text"` (the root-message kind) passes payload
 * through untouched, so this needs NO new Zero column / migration / iOS bump.
 *
 * Authorized for the thread-root author OR a channel manager OR a workspace
 * admin, and ONLY in public workspaces (re-checked inside the tx for TOCTOU).
 * Channel + workspace are DERIVED from the message, never trusted from the
 * caller (Pattern 10). Writes go through the table-level `tx.mutate.messages
 * .update` so they do NOT re-enter the author-only message edit core (which
 * re-derives preview_text + structured columns).
 */

/**
 * The Q&A keys this feature stamps onto a thread-ROOT message's `payload`
 * JSONB (both optional — absence means "no title" / "unsolved"). The single
 * source of truth for the shape, shared with the client reader (ThreadPanel).
 */
export interface ThreadQaPayload {
  thread_title?: string | null;
  accepted_answer_id?: string | null;
}

/**
 * The thread-root message row, as read inside the mutator tx. Only the
 * fields Q&A curation touches are typed; the row carries more columns.
 * `null` on the client optimistic path when the cache lacks the row.
 */
interface ThreadRootRow {
  id: string;
  channel_id: string;
  user_id: string;
  thread_id: string | null;
  payload: unknown;
}

interface RootCtx {
  root: ThreadRootRow | null;
  workspaceId: string | null;
}

async function assertThreadQaAuth(
  tx: MutateSurface,
  ctx: MutatorCtx,
  rootMessageId: string,
): Promise<RootCtx> {
  const userId = ctx?.sub;
  if (!userId) throw new Error("Unauthorized: missing caller identity");
  const root = await tx.run(zql.messages.where("id", rootMessageId).one());
  const channel = root
    ? await tx.run(zql.channels.where("id", root.channel_id).one())
    : null;

  // Q&A is a workspace-channel feature; DMs have no workspace.
  if (channel && !channel.workspace_id) {
    throw new Error("Q&A is not available in direct messages");
  }
  if (tx.location !== "server") {
    return { root, workspaceId: channel?.workspace_id ?? null };
  }

  if (!root) throw new Error("Message not found");
  if (root.thread_id) throw new Error("Not a thread root");
  if (!channel?.workspace_id) {
    throw new Error("Q&A is not available in direct messages");
  }
  // Public-workspace gate (re-checked here against mid-flight is_public toggle).
  const ws = await tx.run(
    zql.workspaces.where("id", channel.workspace_id).one(),
  );
  if (!ws?.is_public) {
    throw new Error("Q&A is only available in public workspaces");
  }
  // The asker, a channel manager, or a workspace admin may curate.
  const chMember = await tx.run(
    zql.channel_members
      .where("channel_id", root.channel_id)
      .where("user_id", userId)
      .where("removed_at", "IS", null)
      .one(),
  );
  // The asker keeps curation rights only while still an active channel member —
  // a kicked/removed asker may not curate. This grant is checked alongside (not
  // before) the admin/manager grants: an admin who happens to also be the asker
  // but has left the channel still curates via the workspace-admin grant below,
  // matching a non-author admin.
  if (root.user_id === userId && chMember) {
    return { root, workspaceId: channel.workspace_id };
  }
  if (chMember?.is_channel_manager) {
    return { root, workspaceId: channel.workspace_id };
  }
  const wsMember = await tx.run(
    zql.workspace_members
      .where("workspace_id", channel.workspace_id)
      .where("user_id", userId)
      .where("removed_at", "IS", null)
      .one(),
  );
  if (wsMember && ADMIN_PLUS.includes(wsMember.role)) {
    return { root, workspaceId: channel.workspace_id };
  }
  // A removed asker who is neither a manager nor an admin falls through here.
  if (root.user_id === userId) {
    throw new Error("Unauthorized: not an active member of this channel");
  }
  throw new Error(
    "Unauthorized: only the asker, a channel manager, or an admin can curate this thread",
  );
}

// Spread the root's existing payload so a write never wipes other keys.
function mergedPayload(
  root: ThreadRootRow | null,
  patch: Partial<Record<keyof ThreadQaPayload, string | undefined>>,
): ReadonlyJSONObject {
  const base: Record<string, unknown> =
    root?.payload && typeof root.payload === "object"
      ? (root.payload as Record<string, unknown>)
      : {};
  const next: Record<string, unknown> = { ...base, ...patch };
  // Drop cleared keys so they don't persist as null.
  for (const k of Object.keys(patch)) {
    if (next[k] === undefined) delete next[k];
  }
  return next as ReadonlyJSONObject;
}

function acceptedAnswerId(root: ThreadRootRow | null): string | null {
  const payload = root?.payload;
  if (!payload || typeof payload !== "object") return null;
  const value = (payload as Record<string, unknown>).accepted_answer_id;
  return typeof value === "string" && value ? value : null;
}

async function deleteBestAnswerSignals(
  tx: MutateSurface,
  replyMessageId: string,
): Promise<void> {
  const result = await tx.run(
    zql.community_signals
      .where("message_id", replyMessageId)
      .where("signal_type", "best_answer"),
  );
  const rows = Array.isArray(result) ? (result as Array<{ id: string }>) : [];
  for (const row of rows) {
    await tx.mutate.community_signals.delete({ id: row.id });
  }
}

export const threadQaMutators = {
  /** Set (or clear, with an empty string) the thread title on the root. */
  setThreadTitle: defineMutator(
    z.object({ rootMessageId: z.string(), title: z.string().max(300) }),
    async ({ tx, args, ctx }) => {
      const { root } = await assertThreadQaAuth(tx, ctx, args.rootMessageId);
      const title = args.title.trim();
      const payload = mergedPayload(root, {
        thread_title: title || undefined,
      });
      await tx.mutate.messages.update({ id: args.rootMessageId, payload });
    },
  ),

  /** Mark one reply as the thread's accepted answer (solved state). */
  acceptAnswer: defineMutator(
    z.object({ rootMessageId: z.string(), replyMessageId: z.string() }),
    async ({ tx, args, ctx }) => {
      const { root, workspaceId } = await assertThreadQaAuth(
        tx,
        ctx,
        args.rootMessageId,
      );
      const userId = ctx.sub;
      if (!userId) throw new Error("Unauthorized: missing caller identity");
      let reply: ThreadRootRow | null = null;
      if (tx.location === "server") {
        reply =
          (await tx.run(zql.messages.where("id", args.replyMessageId).one())) ??
          null;
        if (!reply || reply.thread_id !== args.rootMessageId) {
          throw new Error("Reply is not part of this thread");
        }
        if (reply.user_id === userId) {
          throw new Error("You cannot accept your own answer");
        }
        const previousAnswerId = acceptedAnswerId(root);
        if (previousAnswerId && previousAnswerId !== args.replyMessageId) {
          await deleteBestAnswerSignals(tx, previousAnswerId);
        }
        await deleteBestAnswerSignals(tx, args.replyMessageId);
        await tx.mutate.community_signals.upsert({
          id: crypto.randomUUID(),
          workspace_id: workspaceId ?? "",
          channel_id: root?.channel_id ?? reply.channel_id,
          message_id: args.replyMessageId,
          user_id: userId,
          signal_type: "best_answer",
          created_at: Date.now(),
        });
      }
      const payload = mergedPayload(root, {
        accepted_answer_id: args.replyMessageId,
      });
      await tx.mutate.messages.update({ id: args.rootMessageId, payload });
    },
  ),

  /** Re-open the thread (clear the accepted answer). */
  clearAcceptedAnswer: defineMutator(
    z.object({ rootMessageId: z.string() }),
    async ({ tx, args, ctx }) => {
      const { root } = await assertThreadQaAuth(tx, ctx, args.rootMessageId);
      if (tx.location === "server") {
        const previousAnswerId = acceptedAnswerId(root);
        if (previousAnswerId) {
          await deleteBestAnswerSignals(tx, previousAnswerId);
        }
      }
      const payload = mergedPayload(root, { accepted_answer_id: undefined });
      await tx.mutate.messages.update({ id: args.rootMessageId, payload });
    },
  ),
};
